import { parsePhoneNumber } from 'libphonenumber-js';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { TransferToEscalationOption } from '@/proto/logiblocks/logiblocks';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { collectErrors, validateRequiredString } from '../../validation';
import { TransferToActionCustomization } from './TransferToActionCustomization';
import { defaultNodeData } from '../../graphUtils';

export const TransferToActionFields = {
  PRIMARY_NUMBER: 'Primary number',
  SECOND_NUMBER: 'Secondary number',
  AI_AGENT: 'AI agent',
} as const;

const TransferToIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M17 12L22 7L17 2V5H13V9H17V12ZM19 16.5C17.75 16.5 16.55 16.3 15.43 15.93C15.08 15.82 14.69 15.9 14.41 16.17L12.21 18.37C9.38 16.93 7.06 14.62 5.62 11.78L7.82 9.57C8.1 9.31 8.18 8.92 8.07 8.57C7.7 7.45 7.5 6.25 7.5 5C7.5 4.45 7.05 4 6.5 4H3C2.45 4 2 4.45 2 5C2 14.39 9.61 22 19 22C19.55 22 20 21.55 20 21V17.5C20 16.95 19.55 16.5 19 16.5Z'
      fill='#D1899F'
    />
  </svg>
);

export const TransferToActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'transfer_to_action',

  category: NodeCategory.ACTION,

  label: 'Transfer To',

  hasInput: true,

  getIcon: (size: number) => <TransferToIcon size={size} />,

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const d = data.transfer_to_action;
    if (!d?.primary_number) return undefined;
    try {
      const parsed = parsePhoneNumber(d.primary_number);
      return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
    } catch {
      return d.primary_number;
    }
  },

  getValidationErrors: (
    _nodeId: string,
    nodeData: LBNodeData,
    _graph: GraphData
  ) => {
    const d = nodeData.transfer_to_action;
    return collectErrors(
      validateRequiredString(
        d?.primary_number,
        TransferToActionFields.PRIMARY_NUMBER
      ),
      d?.enable_escalation &&
        d.escalation_option === TransferToEscalationOption.TRY_SECOND_NUMBER
        ? validateRequiredString(
            d.second_number,
            TransferToActionFields.SECOND_NUMBER
          )
        : undefined,
      d?.enable_escalation &&
        d.escalation_option === TransferToEscalationOption.CONNECT_TO_AGENT
        ? validateRequiredString(d.agent_id, TransferToActionFields.AI_AGENT)
        : undefined
    );
  },

  CustomizationComponent: TransferToActionCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('transfer_to_action', {
      primary_number: '',
      enable_escalation: false,
      escalation_option:
        TransferToEscalationOption.TRANSFER_TO_ESCALATION_OPTION_UNSPECIFIED,
      second_number: '',
      agent_id: '',
    }),

  showCustomizePanelOnCreation: true,
};
